import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import styles from './Input.module.css';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  label?: string | undefined;
  error?: string | undefined;
  hint?: string | undefined;
  prefix?: ReactNode;
  suffix?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, prefix, suffix, className, id, ...props }, ref) => {
    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, '-');
    return (
      <div className={styles.wrapper}>
        {label && (
          <label htmlFor={inputId} className={styles.label}>
            {label}
          </label>
        )}
        <div className={[styles.inputWrapper, error ? styles.inputError : ''].join(' ')}>
          {prefix && <span className={styles.prefix}>{prefix}</span>}
          <input
            ref={ref}
            id={inputId}
            className={[styles.input, className ?? ''].join(' ')}
            {...props}
          />
          {suffix && <span className={styles.suffix}>{suffix}</span>}
        </div>
        {error && <p className={styles.error}>{error}</p>}
        {hint && !error && <p className={styles.hint}>{hint}</p>}
      </div>
    );
  }
);
Input.displayName = 'Input';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: string;
  /** Explains the consequence of ticking it — shown under the label. */
  hint?: string | undefined;
}

/**
 * Boolean field. The label sits beside the box (not above it like Input's) so a
 * column of checkboxes reads as a list of statements rather than a form grid.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, hint, id, className, ...props }, ref) => {
    const inputId = id ?? `cb-${label.toLowerCase().replace(/\s+/g, '-')}`;
    return (
      <div className={styles.checkboxWrapper}>
        <input
          ref={ref}
          id={inputId}
          type="checkbox"
          className={[styles.checkbox, className ?? ''].join(' ')}
          {...props}
        />
        <div className={styles.checkboxText}>
          <label htmlFor={inputId} className={styles.checkboxLabel}>{label}</label>
          {hint && <p className={styles.hint}>{hint}</p>}
        </div>
      </div>
    );
  }
);
Checkbox.displayName = 'Checkbox';

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  options: Array<{ value: string; label: string; disabled?: boolean }>;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, options, id, className, ...props }, ref) => {
    const selectId = id ?? label?.toLowerCase().replace(/\s+/g, '-');
    return (
      <div className={styles.wrapper}>
        {label && (
          <label htmlFor={selectId} className={styles.label}>{label}</label>
        )}
        <select
          ref={ref}
          id={selectId}
          className={[styles.select, error ? styles.inputError : '', className ?? ''].join(' ')}
          {...props}
        >
          {options.map(opt => (
            <option key={opt.value} value={opt.value} disabled={opt.disabled}>{opt.label}</option>
          ))}
        </select>
        {error && <p className={styles.error}>{error}</p>}
      </div>
    );
  }
);
Select.displayName = 'Select';
